import { Box, LinearProgress, Tooltip, Typography } from "@mui/material";
import INVOICE_STATUS_OPTIONS_COLORS from "./../enums/invoiceStatusOptionsColors";

const colorOf = (status) => {
    const hit = INVOICE_STATUS_OPTIONS_COLORS.find(
        (x) => String(x.label).toUpperCase() === String(status).toUpperCase()
    );
    return hit ? hit.color : "#cbd5e1";
};

const capitalize = (s) => s.charAt(0) + s.slice(1).toLowerCase();

export default function InvoiceStatusBar({ projectInfo }) {
    const {
        totalInvoices = 0,
        invoicesByStatus = {},
        maxInvoicesPerProject = 0,
        remainingInvoices = 0,
    } = projectInfo || {};

    const usagePct = maxInvoicesPerProject > 0
        ? Math.min(Math.round((totalInvoices / maxInvoicesPerProject) * 100), 100)
        : 0;

    const statusEntries = Object.entries(invoicesByStatus).filter(([, count]) => count > 0);
    const barTotal = Math.max(statusEntries.reduce((sum, [, c]) => sum + c, 0), 1);

    return (
        <Box>
            {/* ── header with accent bar ── */}
            <Box sx={{ borderLeft: '3px solid #427898', pl: 1.5, py: 0.25, mb: 2.5 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1a2332', lineHeight: 1.2 }}>
                    Invoice Summary
                </Typography>
            </Box>

            {/* ── quota usage ── */}
            <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography variant="h5" fontWeight={700} sx={{ color: '#1a2332', lineHeight: 1 }}>
                            {totalInvoices}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                            / {maxInvoicesPerProject}
                        </Typography>
                    </Box>
                    <Typography variant="caption" fontWeight={700} sx={{ color: '#427898' }}>
                        {usagePct}%
                    </Typography>
                </Box>

                <LinearProgress
                    variant="determinate"
                    value={usagePct}
                    sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#e8edf2',
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: usagePct >= 100 ? '#ef4444' : '#427898',
                            borderRadius: 3,
                        },
                    }}
                />

                <Typography variant="caption" sx={{ color: '#64748b', mt: 0.75, display: 'block' }}>
                    {remainingInvoices} invoice{remainingInvoices !== 1 ? 's' : ''} remaining
                </Typography>
            </Box>

            {/* ── status breakdown ── */}
            {statusEntries.length > 0 && (
                <Box sx={{ pt: 2, borderTop: '1px solid #f1f5f9' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
                        By Status
                    </Typography>

                    {/* stacked bar */}
                    <Tooltip
                        title={statusEntries.map(([s, c]) => `${capitalize(s)}: ${c}`).join(' · ')}
                        arrow
                    >
                        <Box sx={{ display: 'flex', height: 8, borderRadius: 999, overflow: 'hidden', mb: 1.5, backgroundColor: '#e8edf2' }}>
                            {statusEntries.map(([status, count]) => (
                                <Box
                                    key={status}
                                    sx={{ width: `${(count / barTotal) * 100}%`, backgroundColor: colorOf(status) }}
                                />
                            ))}
                        </Box>
                    </Tooltip>

                    {/* row per status */}
                    {statusEntries.map(([status, count], i) => (
                        <Box
                            key={status}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 0.75,
                                borderBottom: i < statusEntries.length - 1 ? '1px solid #f1f5f9' : 'none',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colorOf(status), flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ color: '#1a2332', fontWeight: 500 }}>
                                    {capitalize(status)}
                                </Typography>
                            </Box>
                            <Typography variant="body2" fontWeight={700} sx={{ color: '#1a2332' }}>
                                {count}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {statusEntries.length === 0 && totalInvoices === 0 && (
                <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center', py: 1 }}>
                    No invoices yet
                </Typography>
            )}
        </Box>
    );
}
